import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { getTranslation } from '../../../utils/translations';

const emptyForm = {
  userId: '',
  roleId: '',
  countryId: '',
  regionAccess: '',
};

const AssignRoleModal = ({
  isOpen,
  onClose,
  onAssign,
  users,
  roles,
  countries,
  language = 'FR',
}) => {
  const tRoot = getTranslation(language);
  const a = tRoot?.adminAssignRole || {};

  const [formData, setFormData] = useState(emptyForm);
  const [userListFilter, setUserListFilter] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(emptyForm);
    setUserListFilter('');
    setErrors({});
    setIsSubmitting(false);
  }, [isOpen]);

  const userOptions = useMemo(() => {
    const q = userListFilter.trim().toLowerCase();
    const base =
      users?.map((user) => ({
        value: user?.id,
        label: `${user?.fullName || user?.name || ''} (${user?.email || ''})`.trim(),
      })) || [];
    if (!q) return base;
    return base.filter((opt) => opt.label?.toLowerCase()?.includes(q));
  }, [users, userListFilter]);

  const roleOptions =
    roles?.map((role) => ({
      value: role?.id,
      label: role?.roleName,
    })) || [];

  const countryOptions = [
    { value: '', label: a.noCountryRestriction || '' },
    ...(countries?.map((country) => ({
      value: country?.id,
      label: country?.countryName,
    })) || []),
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.userId) {
      newErrors.userId = a?.errors?.userRequired;
    }

    if (!formData?.roleId) {
      newErrors.roleId = a?.errors?.roleRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const regionAccessArray = formData?.regionAccess
        ? formData?.regionAccess?.split(',')?.map((r) => r?.trim())?.filter(Boolean)
        : [];

      await onAssign({
        userId: formData?.userId,
        roleId: formData?.roleId,
        countryId: formData?.countryId || null,
        regionAccess: regionAccessArray,
      });

      setFormData(emptyForm);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error assigning role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md animate-slide-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">{a.title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label={a.userSearchPlaceholder}
            type="search"
            value={userListFilter}
            onChange={(e) => setUserListFilter(e?.target?.value)}
          />

          <Select
            label={a.selectUser}
            options={userOptions}
            value={formData?.userId}
            onChange={(value) => handleChange('userId', value)}
            error={errors?.userId}
            required
          />

          <Select
            label={a.selectRole}
            options={roleOptions}
            value={formData?.roleId}
            onChange={(value) => handleChange('roleId', value)}
            error={errors?.roleId}
            required
          />

          <Select
            label={a.countryOptional}
            options={countryOptions}
            value={formData?.countryId}
            onChange={(value) => handleChange('countryId', value)}
            description={a.countryDescription}
          />

          <Input
            label={a.regionOptional}
            type="text"
            placeholder={a.regionPlaceholder}
            value={formData?.regionAccess}
            onChange={(e) => handleChange('regionAccess', e?.target?.value)}
            description={a.regionDescription}
          />

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isSubmitting}
              iconName="UserPlus"
              iconPosition="left"
              iconSize={18}
            >
              {a.submit}
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={isSubmitting}
            >
              {tRoot?.common?.cancel || 'Cancel'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignRoleModal;
